import React, { useState, useMemo, useEffect } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { Creators as WeightGainProjectionActions } from "../../../store/ducks/weightGainProjection";
import CanvasJSReact from "../../../assets/canvasjs.react";
import {
  Row,
  Col,
  Input,
  Select,
  Icon,
  Drawer,
  Spin,
  notification,
} from "antd";
import NumberFormat from "react-number-format";
import ButtonStandard from "../../utils/button";
import { RadioButtonCustom, RadioGroupCustom } from "../supplier/styles";
import { getSupplementIndexDropDown } from "../../../services/supplementService";

const { CanvasJSChart } = CanvasJSReact;
const { Option } = Select;

const FORM_INITIAL_VALUES = (groupId, farmId) => ({
  id: null,
  groupId,
  farmId,
  name: "",
  informType: "PERIOD",
  timeUnitReference: "DAY",
  fixedGmdWeight: null,
  supplementId: null,
  gainPeriods: [],
});

function buildPeriodChartData(gainPeriods) {
  if (!gainPeriods || gainPeriods.length === 0) return [{ x: 0, y: 0 }];
  const sorted = [...gainPeriods].sort((a, b) => a.period - b.period);
  let prev = 0;
  const pts = [{ x: 0, y: 0 }];
  if (sorted.length === 1) {
    if (sorted[0].period > 1) {
      for (let x = prev + 1; x <= sorted[0].period; x++) {
        pts.push({ x, y: parseFloat(sorted[0].gmdWeight.toFixed(2)) });
      }
    } else {
      pts.push({
        x: sorted[0].period,
        y: parseFloat(sorted[0].gmdWeight.toFixed(2)),
      });
    }
    return pts;
  }
  sorted.forEach(({ period, gmdWeight }, index) => {
    for (let x = prev + 1; x <= period; x++) {
      pts.push({ x, y: parseFloat(gmdWeight.toFixed(2)) });
    }
    prev = period;
  });
  return pts;
}

function buildFixedChartData(fixedGmdWeight) {
  if (!fixedGmdWeight || fixedGmdWeight <= 0) return [{ x: 0, y: 0 }];
  return Array.from({ length: 91 }, (_, i) => ({
    x: i,
    y: parseFloat(fixedGmdWeight.toFixed(2)),
  }));
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required(),
  informType: Yup.string().required(),
  timeUnitReference: Yup.string().when("informType", {
    is: "PERIOD",
    then: Yup.string().required(),
    otherwise: Yup.string().nullable(),
  }),
  fixedGmdWeight: Yup.number().when("informType", {
    is: "FIXED",
    then: Yup.number().moreThan(0).required(),
    otherwise: Yup.number().nullable(),
  }),
  gainPeriods: Yup.array().when("informType", {
    is: "PERIOD",
    then: Yup.array()
      .of(
        Yup.object().shape({
          period: Yup.number().required().min(1),
          gmdWeight: Yup.number().required().moreThan(0),
        }),
      )
      .min(1),
    otherwise: Yup.array(),
  }),
});

const WeightGainProjectionDrawer = () => {
  const dispatch = useDispatch();

  const [newPeriod, setNewPeriod] = useState("");
  const [newGmdWeight, setNewGmdWeight] = useState(null);
  const [supplements, setSupplements] = useState([]);

  const { translation, groupSelected, farmSelected } = useSelector(
    (s) => s.app,
  );
  const { drawerVisible, weightGainProjectionData, isLoading } = useSelector(
    (s) => s.weightGainProjection,
  );

  const actions = useMemo(
    () => bindActionCreators(WeightGainProjectionActions, dispatch),
    [dispatch],
  );

  const t = translation.weightGainProjection;

  const groupId = groupSelected?.id;
  const farmId = farmSelected?.id;

  useEffect(() => {
    async function fetchSupplements() {
      try {
        const {
          data: { results },
        } = await getSupplementIndexDropDown({ groupId, farmId });
        setSupplements(results);
      } catch {
        setSupplements([]);
      }
    }
    if (groupId && farmId) fetchSupplements();
  }, [groupId, farmId]);

  const initialValues = weightGainProjectionData
    ? { ...weightGainProjectionData }
    : FORM_INITIAL_VALUES(groupId, farmId);

  const handleClose = () => {
    actions.hideDrawerWeightGainProjection();
    setNewPeriod("");
    setNewGmdWeight(null);
  };

  const handleSubmit = (values) => {
    actions.saveWeightGainProjection(
      values.groupId,
      values.farmId,
      values.id || null,
      values,
      false,
    );
  };

  const handleAddPeriod = (values, setFieldValue) => {
    const periodNum = parseInt(newPeriod, 10);
    const gmdNum = newGmdWeight;
    const gainPeriods = values.gainPeriods || [];

    if (!gmdNum || gmdNum <= 0) {
      notification.error({ message: t.form.gmdWeight });
      return;
    }
    setFieldValue("gainPeriods", [
      ...gainPeriods,
      { period: periodNum, gmdWeight: gmdNum },
    ]);
    setNewPeriod("");
    setNewGmdWeight(null);
  };

  const handleDeleteLastPeriod = (values, setFieldValue) => {
    const gainPeriods = values.gainPeriods || [];
    if (gainPeriods.length === 0) return;
    setFieldValue("gainPeriods", gainPeriods.slice(0, gainPeriods.length - 1));
  };

  return (
    <Drawer
      title={t.form.title}
      width={900}
      visible={drawerVisible}
      onClose={handleClose}
      maskClosable={false}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        render={({
          values,
          errors,
          submitCount,
          setFieldValue,
          handleSubmit: formikSubmit,
        }) => (
          <form onSubmit={formikSubmit} autoComplete="off">
            <Spin spinning={isLoading}>
              <div
                className="drawerForm"
                style={{ minHeight: "calc(100vh - 165px)" }}
              >
                {/* FORM FIELDS */}
                <Row type="flex" gutter={16}>
                  {/* LEFT COLUMN */}
                  <Col
                    span={values.informType === "PERIOD" ? 12 : 24}
                    style={{
                      borderRight: "1px solid #f0f0f0",
                    }}
                  >
                    {/* Farm / Group radio */}
                    <Row type="flex" style={{ marginTop: 16 }}>
                      <Col xs={24}>
                        <Row>
                          <label
                            style={{ color: "#9074bf", fontWeight: "bold" }}
                          >
                            {t.form.radiogroup.farm} / {t.form.radiogroup.group}
                          </label>
                        </Row>
                        <Row style={{ marginTop: 6 }}>
                          <RadioGroupCustom
                            value={
                              values.groupId != null && values.farmId != null
                                ? "farm"
                                : "group"
                            }
                            buttonStyle="solid"
                          >
                            <RadioButtonCustom
                              value="farm"
                              onChange={() => {
                                setFieldValue("groupId", groupId);
                                setFieldValue("farmId", farmId);
                              }}
                            >
                              {t.form.radiogroup.farm}
                            </RadioButtonCustom>
                            <RadioButtonCustom
                              value="group"
                              onChange={() => {
                                setFieldValue("groupId", groupId);
                                setFieldValue("farmId", null);
                              }}
                            >
                              {t.form.radiogroup.group}: {groupSelected?.name}
                            </RadioButtonCustom>
                          </RadioGroupCustom>
                        </Row>
                      </Col>
                    </Row>

                    {/* Name */}
                    <Row type="flex" style={{ marginTop: 16 }}>
                      <Col xs={24}>
                        <Row>
                          <label
                            style={{
                              color:
                                errors.name && submitCount > 0
                                  ? "#d44c4c"
                                  : "#9074bf",
                              fontWeight: "bold",
                            }}
                          >
                            {t.form.name}*
                          </label>
                        </Row>
                        <Row style={{ marginTop: 6 }}>
                          <Input
                            value={values.name}
                            placeholder={t.form.namePlaceholder}
                            onChange={(e) =>
                              setFieldValue("name", e.target.value)
                            }
                          />
                        </Row>
                      </Col>
                    </Row>

                    {/* supplementId */}
                    <Row type="flex" style={{ marginTop: 16 }}>
                      <Col xs={24}>
                        <Row>
                          <label
                            style={{ color: "#9074bf", fontWeight: "bold" }}
                          >
                            {t.form.supplement}
                          </label>
                        </Row>
                        <Row style={{ marginTop: 6 }}>
                          <Select
                            value={values.supplementId || undefined}
                            placeholder={t.form.supplementPlaceholder}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                              option.props.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                            onChange={(value) =>
                              setFieldValue("supplementId", value || null)
                            }
                            style={{ width: "100%" }}
                          >
                            {supplements.map((s) => (
                              <Option key={s.id} value={s.id}>
                                {s.name}
                              </Option>
                            ))}
                          </Select>
                        </Row>
                      </Col>
                    </Row>

                    {/* informType */}
                    <Row type="flex" style={{ marginTop: 16 }}>
                      <Col xs={24}>
                        <Row>
                          <label
                            style={{
                              color:
                                errors.informType && submitCount > 0
                                  ? "#d44c4c"
                                  : "#9074bf",
                              fontWeight: "bold",
                            }}
                          >
                            {t.form.informType}*
                          </label>
                        </Row>
                        <Row style={{ marginTop: 6 }}>
                          <Select
                            value={values.informType}
                            placeholder={t.form.informType}
                            onChange={(value) => {
                              setFieldValue("informType", value);
                              setFieldValue("gainPeriods", []);
                              setFieldValue("fixedGmdWeight", null);
                              setFieldValue("timeUnitReference", null);
                              setNewPeriod("");
                              setNewGmdWeight(null);
                            }}
                            style={{ width: "100%" }}
                          >
                            <Option value="PERIOD">
                              {t.informType.period}
                            </Option>
                            <Option value="FIXED">{t.informType.fixed}</Option>
                          </Select>
                        </Row>
                      </Col>
                    </Row>

                    {/* timeUnitReference — only PERIOD */}
                    {values.informType === "PERIOD" && (
                      <Row type="flex" style={{ marginTop: 16 }}>
                        <Col xs={24}>
                          <Row>
                            <label
                              style={{
                                color:
                                  errors.timeUnitReference && submitCount > 0
                                    ? "#d44c4c"
                                    : "#9074bf",
                                fontWeight: "bold",
                              }}
                            >
                              {t.form.timeUnitReference}*
                            </label>
                          </Row>
                          <Row style={{ marginTop: 6 }}>
                            <Select
                              value={values.timeUnitReference}
                              placeholder={t.form.timeUnitReference}
                              onChange={(value) => {
                                setFieldValue("timeUnitReference", value);
                                setFieldValue("gainPeriods", []);
                                setNewPeriod("");
                                setNewGmdWeight(null);
                              }}
                              style={{ width: "100%" }}
                            >
                              <Option value="DAY">{t.timeUnit.day}</Option>
                              <Option value="WEEK">{t.timeUnit.week}</Option>
                            </Select>
                          </Row>
                        </Col>
                      </Row>
                    )}

                    {/* fixedGmdWeight — only FIXED */}
                    {values.informType === "FIXED" && (
                      <Row type="flex" style={{ marginTop: 16 }}>
                        <Col xs={24}>
                          <Row>
                            <label
                              style={{
                                color:
                                  errors.fixedGmdWeight && submitCount > 0
                                    ? "#d44c4c"
                                    : "#9074bf",
                                fontWeight: "bold",
                              }}
                            >
                              {t.form.fixedGmdWeight}*
                            </label>
                          </Row>
                          <Row style={{ marginTop: 6 }}>
                            <NumberFormat
                              customInput={Input}
                              value={
                                values.fixedGmdWeight === null
                                  ? ""
                                  : values.fixedGmdWeight
                              }
                              decimalScale={2}
                              allowNegative={false}
                              decimalSeparator=","
                              thousandSeparator="."
                              fixedDecimalScale
                              placeholder={t.form.fixedGmdWeight}
                              onValueChange={({ floatValue }) =>
                                setFieldValue(
                                  "fixedGmdWeight",
                                  floatValue !== undefined ? floatValue : null,
                                )
                              }
                            />
                          </Row>
                        </Col>
                      </Row>
                    )}

                    {/* Add Period section — only PERIOD */}
                    {values.informType === "PERIOD" && (
                      <>
                        <Row
                          type="flex"
                          gutter={8}
                          style={{ marginTop: 20 }}
                          align="bottom"
                        >
                          <Col xs={24} sm={10}>
                            <Row>
                              <label
                                style={{ color: "#9074bf", fontWeight: "bold" }}
                              >
                                {t.form.period}
                              </label>
                            </Row>
                            <Row style={{ marginTop: 6 }}>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={newPeriod}
                                placeholder={
                                  values.timeUnitReference === "WEEK"
                                    ? t.timeUnit.week
                                    : t.timeUnit.day
                                }
                                onChange={(e) => setNewPeriod(e.target.value)}
                              />
                            </Row>
                          </Col>
                          <Col xs={24} sm={10}>
                            <Row>
                              <label
                                style={{ color: "#9074bf", fontWeight: "bold" }}
                              >
                                {t.form.gmdWeight}
                              </label>
                            </Row>
                            <Row style={{ marginTop: 6 }}>
                              <NumberFormat
                                customInput={Input}
                                value={
                                  newGmdWeight === null ? "" : newGmdWeight
                                }
                                decimalScale={2}
                                allowNegative={false}
                                decimalSeparator=","
                                thousandSeparator="."
                                fixedDecimalScale
                                placeholder={t.form.gmdWeight}
                                onValueChange={({ floatValue }) =>
                                  setNewGmdWeight(
                                    floatValue !== undefined
                                      ? floatValue
                                      : null,
                                  )
                                }
                                onPressEnter={(e) => {
                                  e.preventDefault();
                                  handleAddPeriod(values, setFieldValue);
                                }}
                              />
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={24} style={{ display: "flex" }}>
                            <ButtonStandard
                              type="button"
                              buttonType="type6"
                              style={{ marginTop: 6, width: "100%" }}
                              onClick={() =>
                                handleAddPeriod(values, setFieldValue)
                              }
                            >
                              <Icon type="plus" style={{ marginRight: 4 }} />
                              {t.form.addPeriod}
                            </ButtonStandard>
                          </Col>
                        </Row>
                      </>
                    )}
                  </Col>

                  {/* RIGHT COLUMN — PERIOD TABLE */}
                  {values.informType === "PERIOD" && (
                    <Col span={12}>
                      {/* Periods table */}
                      <div
                        style={{
                          marginTop: 12,
                          borderRadius: 4,
                          overflow: "hidden",
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        {/* Header */}
                        <div
                          style={{
                            display: "flex",
                            background: "#f5f5f5",
                            padding: "8px 12px",
                            fontWeight: "bold",
                            fontSize: 13,
                            color: "#4b4b4b",
                          }}
                        >
                          <span style={{ flex: 1 }}>{t.form.period}</span>
                          <span style={{ flex: 1 }}>{t.form.gmdWeight}</span>
                          <span style={{ width: 100 }} />
                        </div>
                        {/* Rows */}
                        {values.gainPeriods.map((gp, index) => {
                          const isLast =
                            index === values.gainPeriods.length - 1;
                          return (
                            <div
                              key={`period_${index}`}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "6px 12px",
                                borderTop: "1px solid #e8e8e8",
                                fontSize: 13,
                                color: "#4b4b4b",
                              }}
                            >
                              <span style={{ flex: 1 }}>{gp.period}</span>
                              <span style={{ flex: 1 }}>
                                {gp.gmdWeight.toFixed(2).replace(".", ",")}
                              </span>
                              <span
                                style={{
                                  width: 100,
                                  display: "flex",
                                  justifyContent: "flex-end",
                                }}
                              >
                                {isLast && (
                                  <ButtonStandard
                                    type="button"
                                    buttonType="type7"
                                    onClick={() =>
                                      handleDeleteLastPeriod(
                                        values,
                                        setFieldValue,
                                      )
                                    }
                                  >
                                    <Icon type="delete" />
                                  </ButtonStandard>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "#aaa",
                          marginTop: 4,
                        }}
                      >
                        {t.form.deleteLastOnly}
                      </div>
                    </Col>
                  )}
                </Row>
                {/* CHART */}
                <Row type="flex" gutter={16}>
                  <Col span={24} style={{ marginTop: 24 }}>
                    {(() => {
                      const chartData =
                        values.informType === "FIXED"
                          ? buildFixedChartData(values.fixedGmdWeight)
                          : buildPeriodChartData(values.gainPeriods);

                      const xAxisTitle =
                        values.informType === "FIXED"
                          ? t.timeUnit.day
                          : values.timeUnitReference === "WEEK"
                            ? t.timeUnit.week
                            : t.timeUnit.day;

                      const chartOptions = {
                        animationEnabled: false,
                        zoomEnabled: true,
                        height: 380,
                        axisX: {
                          minimum: 0,
                          interval: 1,
                          viewportMinimum: 0,
                          viewportMaximum: 30,
                          crosshair: {
                            enabled: true,
                            snapToDataPoint: true,
                          },
                          title: xAxisTitle,
                          gridDashType: "dot",
                        },
                        axisY: {
                          minimum: 0,
                          suffix: "kg",
                          title: t.form.chart.title,
                          gridDashType: "dot",
                        },
                        data: [
                          {
                            type: "line",
                            markerType: "circle",
                            markerSize: 5,
                            color: "#4F81BC",
                            dataPoints: chartData,
                          },
                        ],
                      };

                      return <CanvasJSChart options={chartOptions} />;
                    })()}
                  </Col>
                </Row>
              </div>
              {/* Footer */}
              <div
                style={{
                  borderTop: "1px solid #e8e8e8",
                  marginTop: 20,
                  width: "100%",
                  padding: "10px 24px",
                }}
              >
                <Row type="flex" justify="end">
                  <Col
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <ButtonStandard
                      type="button"
                      buttonType="type7"
                      style={{ marginRight: 12 }}
                      onClick={handleClose}
                    >
                      {translation.buttons.cancel}
                    </ButtonStandard>
                    <ButtonStandard
                      type="submit"
                      buttonType="type6"
                      isLoading={isLoading}
                    >
                      {translation.buttons.save}
                    </ButtonStandard>
                  </Col>
                </Row>
              </div>
            </Spin>
          </form>
        )}
      />
    </Drawer>
  );
};

export default WeightGainProjectionDrawer;
